<template>
    <!-- Bonus Card Component - displays credit card welcome bonus information -->
    <article
        class="w-full px-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col"
    >
        <!-- Header with bonus type and status -->
        <header class="flex items-center justify-between py-4">
            <div class="flex items-center gap-2">
                <Icon :icon="bonus.type" class="w-5 h-5 text-slate-800 dark:text-slate-200" />
                <h3 class="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {{ formatBonusTitle(bonus.type) }} bonus
                </h3>
            </div>
            <!-- Tiered bonus status -->
            <span
                v-if="bonus.type === 'tiered' && tieredBonusStatus"
                :class="tieredBonusStatus.classes"
            >
                {{ tieredBonusStatus.label }}
            </span>
            <!-- Non-tiered bonus status -->
            <span
                v-if="
                    bonus.type !== 'other' &&
                    bonus.type !== 'tiered' &&
                    bonus.received
                "
                class="px-3 py-0.5 bg-emerald-50 dark:bg-emerald-500/30 text-emerald-500 dark:text-emerald-200 text-xs font-medium rounded-xl h-5 flex items-center shrink-0"
            >
                Received
            </span>
            <span
                v-if="
                    bonus.type !== 'other' &&
                    bonus.type !== 'tiered' &&
                    !bonus.received
                "
                class="px-3 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs font-medium rounded-xl h-5 flex items-center shrink-0"
            >
                Not received
            </span>
        </header>

        <!-- Divider -->
        <hr class="border-slate-200 dark:border-slate-700" />

        <!-- Spend progress (MY1-3042): welcome bonuses only — bonus offers
             keep their current rows. The bar renders nothing for received,
             goal_not_met, suppressed or not-trackable states. -->
        <BonusProgressBar
            v-if="bonusType === 'welcome_bonus' && bonus.type !== 'tiered'"
            :bonus="bonus"
            label-placement="split"
            class="pt-4"
        />

        <!-- Bonus Details -->
        <dl class="py-4 flex flex-col gap-4" v-if="bonus.type !== 'tiered'">
            <!-- Bonus Amount -->
            <div
                class="grid grid-cols-2 gap-1 items-center"
                v-if="bonus.type !== 'other'"
            >
                <dt
                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center"
                >
                    Bonus amount
                </dt>
                <dd
                    class="text-sm text-right sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center justify-end gap-2"
                >
                    {{ formatBonusAmount(bonus) }}
                    <img
                        v-if="programImageUrl"
                        :src="programImageUrl"
                        :alt="program?.name || 'Rewards program'"
                        class="h-4 w-auto max-w-24 object-contain"
                    />
                </dd>
            </div>

            <!-- Bonus Received Date -->
            <div
                class="grid grid-cols-2 gap-1 items-center"
                v-show="bonus.received && bonus.type !== 'other'"
            >
                <dt
                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center"
                >
                    Bonus received
                </dt>
                <dd
                    class="text-sm text-right sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center justify-end"
                >
                    {{ formatDate(bonus.date_received) }}
                </dd>
            </div>

            <!-- Minimum Spend Requirement -->
            <div
                class="grid grid-cols-2 gap-1 items-center"
                v-show="!bonus.received && bonus.type !== 'other'"
            >
                <dt
                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center whitespace-nowrap"
                >
                    Minimum spend amount
                </dt>
                <dd
                    class="text-sm text-right sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center justify-end"
                >
                    {{ formatMinimumSpend(bonus.minimum_spend_amount) }} until
                    {{ formatDate(bonus.minimum_spend_deadline) }}
                </dd>
            </div>
            <!-- Other bonus offers -->
            <div
                class="grid grid-cols-2 gap-1 items-center"
                v-if="bonus.type === 'other'"
            >
                <dt
                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center"
                >
                    Details
                </dt>
                <dd
                    class="text-sm text-left sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center"
                >
                    {{ bonus.details }}
                </dd>
            </div>
        </dl>
        <div class="divide-y divide-slate-200" v-else>
            <dl
                v-for="tier in bonus.tiers"
                :key="tier.id"
                class="flex flex-col gap-4 py-4"
            >
                <!-- Tier Header with Status Badge -->
                <div class="flex items-center justify-between gap-1">
                    <dt
                        class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 flex"
                    >
                        Tier {{ tier.tier_number }}
                    </dt>
                    <dd
                        v-if="tier.received"
                        class="px-3 py-0.5 bg-emerald-50 dark:bg-emerald-500/30 text-emerald-500 dark:text-emerald-200 text-xs font-medium rounded-full h-5 flex items-center w-fit shrink-0"
                    >
                        Received
                    </dd>
                    <dd
                        v-else
                        class="px-3 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs font-medium rounded-full h-5 flex items-center w-fit shrink-0"
                    >
                        Not received
                    </dd>
                </div>

                <!-- Per-tier spend progress (MY1-3042 / MY1-3039) -->
                <BonusProgressBar
                    v-if="bonusType === 'welcome_bonus'"
                    :bonus="tier"
                    label-placement="split"
                />

                <!-- Per-tier actions (MY1-3043) -->
                <BonusActionStrip
                    v-if="bonusType === 'welcome_bonus'"
                    :bonus-row="tier"
                    :busy="actionBusy"
                    @mark-received="
                        (e) =>
                            $emit('mark-received', {
                                tierId: tier.id,
                                event: e,
                            })
                    "
                    @goal-not-met="
                        (e) =>
                            $emit('goal-not-met', { tierId: tier.id, event: e })
                    "
                />

                <!-- Bonus Amount -->
                <div class="grid grid-cols-2 gap-1 items-center">
                    <dt
                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center"
                    >
                        Bonus amount
                    </dt>
                    <dd
                        class="text-sm text-right sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center justify-end gap-2"
                    >
                        {{ formatBonusAmount(tier) }}
                        <img
                            v-if="programImageUrl"
                            :src="programImageUrl"
                            :alt="program?.name || 'Rewards program'"
                            class="h-4 w-auto max-w-24 object-contain"
                        />
                    </dd>
                </div>

                <!-- Bonus Received Date -->
                <div
                    class="grid grid-cols-2 gap-1 items-center"
                    v-show="tier.received"
                >
                    <dt
                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center"
                    >
                        Bonus received
                    </dt>
                    <dd
                        class="text-sm text-right sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center justify-end"
                    >
                        {{ formatDate(tier.date_received) }}
                    </dd>
                </div>

                <!-- Minimum Spend Requirement -->
                <div
                    class="grid grid-cols-2 gap-1 items-center"
                    v-show="!tier.received"
                >
                    <dt
                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center whitespace-nowrap"
                    >
                        Minimum spend amount
                    </dt>
                    <dd
                        class="text-sm text-right sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center justify-end"
                    >
                        {{ formatMinimumSpend(tier.minimum_spend_amount) }}
                        until
                        {{ formatDate(tier.minimum_spend_deadline) }}
                    </dd>
                </div>
            </dl>
        </div>

        <!-- State-dependent actions (MY1-3043, welcome bonuses only). The
             strip is a shaded footer inside the card; tiers carry their own
             inside each section above. -->
        <div
            v-if="bonusType === 'welcome_bonus' && bonus.type !== 'tiered'"
            class="pb-4 empty:hidden"
        >
            <BonusActionStrip
                :bonus-row="bonus"
                :busy="actionBusy"
                @mark-received="
                    (e) => $emit('mark-received', { tierId: null, event: e })
                "
                @goal-not-met="
                    (e) => $emit('goal-not-met', { tierId: null, event: e })
                "
            />
        </div>

        <!-- Connect tip (MY1-3044): trackable but unconnected — always shown,
             survives the banner's dismissal. -->
        <div
            v-if="showConnectTip"
            class="mb-4 rounded-lg bg-slate-50 dark:bg-slate-700/50 px-4 py-3 flex items-center justify-between gap-3"
        >
            <div class="flex items-center gap-2 min-w-0">
                <Icon
                    icon="bank-financial"
                    class="w-5 h-5 shrink-0 text-slate-800 dark:text-slate-100"
                />
                <p class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100">
                    Connect bank and track welcome bonus automatically
                </p>
            </div>
            <button
                type="button"
                class="shrink-0 px-4 py-2 rounded-[4px] border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150 text-sm font-medium text-slate-800 dark:text-slate-100"
                @click="$emit('connect-bank')"
            >
                Connect
            </button>
        </div>

        <!-- Another member's card (MY1-3077): the opt-in belongs to its owner,
             so say why the bar reads $0 instead of leaving it unexplained. -->
        <div
            v-if="showSpendingDisabledNote"
            class="mb-4 rounded-lg bg-slate-50 dark:bg-slate-700/50 px-4 py-3 flex items-start gap-2"
        >
            <Icon
                icon="calendar-check"
                class="w-5 h-5 shrink-0 text-slate-500 dark:text-slate-400"
            />
            <p class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400">
                Not tracked. {{ spendingOwnerLabel }} has My Spending turned off
            </p>
        </div>

        <!-- Turn-on-My-Spending prompt (MY1-3045): connected but the user
             hasn't enabled My Spending, so tracking can't run. Mutually
             exclusive with the connect tip; the bar stays visible at $0. -->
        <div
            v-if="showSpendingPrompt"
            class="mb-4 rounded-lg bg-slate-50 dark:bg-slate-700/50 px-4 py-3 flex flex-col gap-3"
        >
            <div class="flex items-start gap-2">
                <Icon
                    icon="calendar-check"
                    class="w-5 h-5 shrink-0 text-slate-800 dark:text-slate-100"
                />
                <p class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100">
                    One more thing. Turn on My Spending so we could get
                    spending goal progress.
                </p>
            </div>
            <div class="flex items-center gap-4">
                <!-- MY1-3077: this card is already bank-connected — the opt-in
                     is the only thing missing, so turn it on from here rather
                     than sending the user to /spending and losing their place.
                     "Learn more" still goes to the page. -->
                <button
                    type="button"
                    class="px-4 py-2 rounded-[4px] border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150 text-sm font-medium text-slate-800 dark:text-slate-100 disabled:opacity-60 disabled:cursor-not-allowed"
                    :disabled="actionBusy"
                    @click="$emit('enable-spending')"
                >
                    Turn on My Spending
                </button>
                <a
                    href="/spending"
                    class="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
                >
                    Learn more
                </a>
            </div>
        </div>
    </article>
</template>
<script setup>
import { computed } from 'vue';
import dayjs from 'dayjs';
import Icon from '@/components/ui/icon/icon.vue';
import BonusProgressBar from '@/components/wallet/partials/bonus-progress-bar.vue';
import BonusActionStrip from '@/components/wallet/partials/bonus-action-strip.vue';
import filters from '@/filters';

defineEmits(['mark-received', 'goal-not-met', 'connect-bank', 'enable-spending']);

const props = defineProps({
    bonus: {
        type: Object,
        required: true,
    },
    bonusType: {
        type: String,
        required: true,
        validator: (value) => {
            return ['bonus_offer', 'welcome_bonus'].includes(value);
        },
    },
    bonusTiers: {
        type: Array,
        required: false,
    },
    // The card's rewards program (with programImage) — shown next to the
    // bonus amount for welcome bonuses (MY1-3042).
    program: {
        type: Object,
        required: false,
        default: null,
    },
    // MY1-3043: disables the action-strip buttons while a request is in flight.
    actionBusy: {
        type: Boolean,
        default: false,
    },
    // MY1-3077: whether the signed-in user owns this card. /spending/enable
    // only ever mutates the signed-in user, so on a shared wallet the opt-in
    // is meaningless on another member's card — it would change the viewer's
    // own preference and leave that card's prompt exactly where it was. The
    // parent resolves ownership; this hides the prompt when it is not theirs.
    canEnableSpending: {
        type: Boolean,
        default: true,
    },
    // MY1-3077: the card owner's display name, used only to explain the $0 bar
    // on someone else's card. Null falls back to a generic phrasing.
    spendingOwnerName: {
        type: String,
        required: false,
        default: null,
    },
});

// MY1-3044: the quiet connect tip — welcome bonuses on trackable issuers
// without a live connection, regardless of the (dismissible) panel banner.
// Reads the tracking facts the presenter serializes on the bonus (or, for
// tiered bonuses, on any tier — same account, same connection).
const bonusTracking = computed(
    () => props.bonus?.tracking || props.bonus?.tiers?.[0]?.tracking || null
);

const showConnectTip = computed(() => {
    if (props.bonusType !== 'welcome_bonus') {
        return false;
    }

    return Boolean(bonusTracking.value?.trackable) && !bonusTracking.value?.connected;
});

// MY1-3045: connected but My Spending is off — the presenter resolves this
// exact combination to the spending_disabled state, keeping the three widget
// prompts (connect tip / this / none) mutually exclusive.
// MY1-3077: suppressed on another member's card — the whole prompt, not just
// its button, because the copy addresses the reader ("Turn on My Spending so
// we could get…") and no action behind it can affect that card.
const spendingDisabled = computed(
    () =>
        props.bonusType === 'welcome_bonus' &&
        bonusTracking.value?.state === 'spending_disabled'
);

const showSpendingPrompt = computed(
    () => spendingDisabled.value && props.canEnableSpending
);

// The passive counterpart: same state, someone else's card. Explains the $0
// bar rather than leaving it bare, without offering an action that would not
// work. Exactly one of the two ever renders.
const showSpendingDisabledNote = computed(
    () => spendingDisabled.value && !props.canEnableSpending
);

// Falls back to a generic phrasing when the wallet payload carries no name —
// `User::$visible` exposes it, but a card with no owner attached would not.
const spendingOwnerLabel = computed(
    () => props.spendingOwnerName || "This card's owner"
);

const programImageUrl = computed(() => {
    if (props.bonusType !== 'welcome_bonus') {
        return null;
    }

    const image = props.program?.program_image;
    if (!image?.path || !image?.file_name) {
        return null;
    }

    return `${window.cdnUrl || ''}/${image.path}/_small/${image.file_name}`;
});
const formatBonusAmount = (bonus) => {
    if (!bonus || bonus.bonus_amount === null || bonus.bonus_amount === undefined) {
        return '--';
    }
    
    if (bonus.type === 'free_nights') {
        return `${bonus.bonus_amount} certificate${bonus.bonus_amount === 1 ? '' : 's'}`;
    }
    if (
        bonus.type === 'cashback' ||
        bonus.bonus_units === 'dollars' ||
        bonus.bonus_units === 'statement_credits'
    ) {
        return bonus.bonus_amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
    if (
        bonus.type === 'points' ||
        bonus.bonus_units === 'points' ||
        bonus.bonus_units === 'miles'
    ) {
        return filters.formatThousand(bonus.bonus_amount);
    }
    return bonus.bonus_amount;
};

const formatMinimumSpend = (amount) => {
    if (amount === null || amount === undefined) {
        return '--';
    }
    
    const numericAmount = Number(amount);
    
    if (numericAmount === 0) {
        return '$0';
    }
    
    return numericAmount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
};

const formatDate = (date) => {
    if (!date) return '';
    return dayjs(date).format('MMM D, YYYY');
};

const formatBonusTitle = (title) => {
    if (!title) return '';
    return filters.titleCase(title.replace(/_/g, ' '));
};

// Computed property to calculate tiered bonus status
const tieredBonusStatus = computed(() => {
    if (
        props.bonus.type !== 'tiered' ||
        !props.bonusTiers ||
        props.bonusTiers.length === 0
    ) {
        return null;
    }

    const receivedCount = props.bonusTiers.filter(
        (tier) => tier.received
    ).length;
    const totalCount = props.bonusTiers.length;

    // MY1-3042: always the "x of y received" form; green once any tier has
    // landed, matching the wallet-table pill (MY1-3041).
    if (receivedCount === totalCount) {
        return {
            label: 'Received',
            classes:
                'px-3 py-0.5 bg-emerald-50 dark:bg-emerald-500/30 text-emerald-500 dark:text-emerald-200 text-xs font-medium rounded-xl h-5 flex items-center shrink-0',
        };
    }

    return {
        label: `${receivedCount} of ${totalCount} received`,
        classes:
            receivedCount > 0
                ? 'px-3 py-0.5 bg-emerald-50 dark:bg-emerald-500/30 text-emerald-500 dark:text-emerald-200 text-xs font-medium rounded-xl h-5 flex items-center shrink-0'
                : 'px-3 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs font-medium rounded-xl h-5 flex items-center shrink-0',
    };
});
</script>
